/**
 * Robust Extended Breadcrumbs - Menu-Based Approach
 * 
 * Universal version: Tracks all menu navigation, not just collections.
 * Works with products, collections, pages, blog posts, and any other content.
 * Uses data-level attributes to capture parent-child relationships.
 * 
 * Clean approach:
 * - Tracks all menu clicks with data-level attributes
 * - Stores relationships for short duration (30 minutes)
 * - No complex fallback logic or guessing
 * - Uses data-level as authoritative parent-child indicator
 */
class ExtendedBreadcrumbs {
  constructor(options = {}) {
    this.debug = options.debug || false;
    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[ExtendedBreadcrumbs]', ...args);
    }
  }

  init() {
    // Track menu clicks to capture parent-child relationships
    this.setupMenuTracking();
    
    // Enhance breadcrumbs on any page with breadcrumbs
    if (this.hasBreadcrumbs()) {
      this.enhanceBreadcrumbs();
    }
  }

  setupMenuTracking() {
    document.addEventListener('click', (e) => {
      // Look for any links in menu with data-level attributes
      const link = e.target.closest('a');
      if (!link || !link.href) return;

      // Find the data-level - could be on the link itself or parent element
      const level = this.getDataLevel(link);
      if (!level) return;

      this.log('Menu click detected:', {
        url: link.href,
        level: level,
        text: link.textContent.trim()
      });

      this.processMenuClick(link, level);
    });
  }

  getDataLevel(link) {
    // Check the link itself first
    let level = link.getAttribute('data-level');
    if (level) return level;

    // Check parent elements up to 3 levels
    let element = link.parentElement;
    let depth = 0;
    while (element && depth < 3) {
      level = element.getAttribute('data-level');
      if (level) return level;
      element = element.parentElement;
      depth++;
    }

    return null;
  }

  processMenuClick(link, level) {
    const path = this.extractPath(link.href);
    const title = link.textContent.trim();
    
    if (!path || !title) return;

    // Store page title for better breadcrumb display
    this.storePageTitle(path, title);

    // For child items (level 2+), find and store parent relationship
    if (level === '2' || level === '3') {
      const parentLink = this.findMenuParent(link, level);
      if (parentLink) {
        const parentPath = this.extractPath(parentLink.href);
        const parentTitle = parentLink.textContent.trim();

        if (parentPath && parentTitle) {
          this.log('Storing menu relationship:', parentPath, '→', path);
          this.storePageTitle(parentPath, parentTitle);
          this.storeParentRelationship(path, parentPath);
        }
      }
    }
  }

  findMenuParent(childLink, childLevel) {
    const targetLevel = childLevel === '3' ? '2' : '1';
    
    // Start from child link and traverse up the DOM
    let element = childLink;
    
    while (element && element !== document.body) {
      element = element.parentElement;
      
      // Look for parent level links in current context
      const parentCandidate = element.querySelector(`a[data-level="${targetLevel}"]`);
      if (parentCandidate && parentCandidate.href) {
        return parentCandidate;
      }
      
      // Also check if current element has the target level
      if (element.getAttribute('data-level') === targetLevel) {
        const linkInElement = element.querySelector('a');
        if (linkInElement && linkInElement.href) {
          return linkInElement;
        }
      }
    }

    return null;
  }

  enhanceBreadcrumbs() {
    const currentPath = this.getCurrentPath();
    if (!currentPath) return;

    this.log('Checking breadcrumbs for:', currentPath);

    // Look for recent parent relationship from menu navigation
    const parentPath = this.getRecentParent(currentPath);
    if (parentPath) {
      this.log('Adding parent breadcrumb:', parentPath, '→', currentPath);
      this.insertParentBreadcrumb(parentPath);
    } else {
      this.log('No recent parent relationship found for:', currentPath);
    }
  }

  getRecentParent(childPath) {
    try {
      const relationships = JSON.parse(localStorage.getItem('breadcrumbRelationships') || '{}');
      const relationship = relationships[childPath];

      if (relationship && relationship.parent) {
        // Check if relationship is recent (30 minutes)
        const thirtyMinutes = 30 * 60 * 1000;
        const isRecent = Date.now() - relationship.timestamp < thirtyMinutes;
        
        if (isRecent) {
          return relationship.parent;
        } else {
          this.log('Parent relationship expired for:', childPath);
          this.cleanupExpiredRelationship(childPath);
        }
      }
    } catch (e) {
      this.log('Error getting parent relationship:', e);
    }

    return null;
  }

  storeParentRelationship(childPath, parentPath) {
    try {
      let relationships = JSON.parse(localStorage.getItem('breadcrumbRelationships') || '{}');
      
      relationships[childPath] = {
        parent: parentPath,
        timestamp: Date.now()
      };

      // Keep only last 15 relationships to prevent bloat
      const entries = Object.entries(relationships);
      if (entries.length > 15) {
        // Sort by timestamp and keep most recent
        const sorted = entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
        relationships = Object.fromEntries(sorted.slice(0, 15));
      }

      localStorage.setItem('breadcrumbRelationships', JSON.stringify(relationships));
      this.log('Stored relationship:', parentPath, '→', childPath);
    } catch (e) {
      this.log('Error storing relationship:', e);
    }
  }

  cleanupExpiredRelationship(childPath) {
    try {
      let relationships = JSON.parse(localStorage.getItem('breadcrumbRelationships') || '{}');
      if (relationships[childPath]) {
        delete relationships[childPath];
        localStorage.setItem('breadcrumbRelationships', JSON.stringify(relationships));
      }
    } catch (e) {
      // Silent cleanup failure
    }
  }

  insertParentBreadcrumb(parentPath) {
    const breadcrumbs = document.querySelector('.breadcrumbs__list');
    if (!breadcrumbs) return;

    const currentItem = breadcrumbs.querySelector('li:last-child');
    if (!currentItem) return;

    // Don't add if already exists
    if (breadcrumbs.querySelector(`a[href="${parentPath}"]`)) {
      this.log('Parent breadcrumb already exists');
      return;
    }

    const parentTitle = this.getPageTitle(parentPath);

    const parentItem = document.createElement('li');
    parentItem.className = 'breadcrumbs__item';
    parentItem.innerHTML = `
      <a class="breadcrumbs__link" href="${parentPath}">${parentTitle}</a>
      <svg xmlns="http://www.w3.org/2000/svg" width="4" height="8" viewBox="0 0 4 8" fill="none">
        <path d="M3.85354 4.32275C4.04882 4.14423 4.04882 3.85433 3.85354 3.67582L0.85413 0.933881C0.658856 0.755369 0.34173 0.755369 0.146456 0.933881C-0.0488189 1.11239 -0.0488189 1.4023 0.146456 1.58081L2.79281 4L0.148018 6.41919C-0.0472566 6.5977 -0.0472566 6.8876 0.148018 7.06611C0.343292 7.24462 0.660418 7.24462 0.855692 7.06611L3.85511 4.32417L3.85354 4.32275Z" fill="black"/>
      </svg>
    `;

    breadcrumbs.insertBefore(parentItem, currentItem);
    this.log('Parent breadcrumb inserted successfully');
  }

  // Utility functions
  hasBreadcrumbs() {
    return !!document.querySelector('.breadcrumbs__list');
  }

  getCurrentPath() {
    // Get the current page path (pathname without query/hash)
    const path = window.location.pathname;
    // Remove trailing slash for consistency
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  }

  extractPath(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const path = urlObj.pathname;
      // Remove trailing slash for consistency
      return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    } catch (e) {
      return null;
    }
  }

  getPageTitle(path) {
    try {
      const titles = JSON.parse(localStorage.getItem('breadcrumbTitles') || '{}');
      return titles[path] || this.formatPathToTitle(path);
    } catch (e) {
      return this.formatPathToTitle(path);
    }
  }

  formatPathToTitle(path) {
    // Extract the last segment of the path
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || 'Home';
    
    // Convert dashes/underscores to spaces and capitalize
    return lastSegment
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  storePageTitle(path, title) {
    try {
      let titles = JSON.parse(localStorage.getItem('breadcrumbTitles') || '{}');
      titles[path] = title;

      // Keep only last 20 titles
      const entries = Object.entries(titles);
      if (entries.length > 20) {
        titles = Object.fromEntries(entries.slice(-20));
      }

      localStorage.setItem('breadcrumbTitles', JSON.stringify(titles));
    } catch (e) {
      // Silent failure for title storage
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const debugMode = new URLSearchParams(window.location.search).get('breadcrumb_debug') === '1' || 
                   window.breadcrumbDebug === true;
  
  new ExtendedBreadcrumbs({ debug: debugMode });
});